// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Tow Service Utility Functions
 * Pure functions for handling fuel depletion and tow service offers
 */

import GameConfig from '../../config/GameConfig';

/**
 * Calculate tow service data when fuel is depleted
 * @param {Object} currentLocation - Current location with in-transit coordinates
 * @param {Object} destination - Original destination system
 * @param {Array} starSystems - Array of all star systems
 * @param {Function} calculateDistance - Distance calculation function
 * @returns {Object} Tow service data
 */
export const calculateTowServiceData = (currentLocation, destination, starSystems, calculateDistance) => {
  // Find nearest star system with refuel services
  let nearestStar = null;
  let minDistance = Infinity;
  
  starSystems.forEach(system => {
    if (system.name === 'Sol') return; // Skip Sol as it's the origin
    
    // Only consider systems with refuel services
    if (!system.services || !system.services.includes('refuel')) return;
    
    const distance = calculateDistance(currentLocation, system);
    if (distance < minDistance) {
      minDistance = distance;
      nearestStar = system;
    }
  });
  
  // Calculate distances
  const distanceTraveled = calculateDistance({ name: 'Sol', distance: 0 }, currentLocation);
  const distanceRemaining = calculateDistance(currentLocation, destination);
  
  return {
    nearestStar,
    distanceToNearest: minDistance,
    distanceTraveled: distanceTraveled.toFixed(2),
    distanceRemaining: distanceRemaining.toFixed(2),
    originalDestination: destination
  };
};

/**
 * Create stranded event object
 * @param {Object} towData - Tow service data
 * @param {Array} actions - Action objects
 * @returns {Object} Event object for stranded state
 */
export const createStrandedEvent = (towData, actions) => {
  const { nearestStar, distanceTraveled, distanceRemaining, originalDestination } = towData;
  const towRefuelPercent = GameConfig.credits.towRefuelPercent;
  
  return {
    type: 'STRANDED',
    title: '⚠️ FUEL DEPLETED',
    message: `Antimatter reserves exhausted! Ship has dropped out of warp.\n\nTraveled: ${distanceTraveled} LY\nRemaining to ${originalDestination.name}: ${distanceRemaining} LY\n\nNearest station: ${nearestStar.name}\n\n🆓 FREE TOW SERVICE AVAILABLE\nIncludes refuel to ${towRefuelPercent}%`,
    color: '#ff4444',
    icon: '⛽',
    actions
  };
};

/**
 * Build tow service event actions
 * @param {string} nearestStarName - Name of nearest star
 * @param {Function} onAcceptTow - Callback for accepting tow
 * @param {Function} onDeclineTow - Callback for declining tow
 * @returns {Array} Array of action objects
 */
export const buildTowServiceActions = (nearestStarName, onAcceptTow, onDeclineTow) => {
  return [
    {
      label: `FREE TOW TO ${nearestStarName}`,
      feedback: '📡 DISTRESS SIGNAL SENT - Tow ship en route',
      effect: onAcceptTow
    },
    {
      label: 'DECLINE (STAY STRANDED)',
      feedback: '🛸 REMAINING IN DEEP SPACE',
      effect: onDeclineTow
    }
  ];
};

/**
 * Create tow completion feedback message
 * @param {string} destinationName - Name of tow destination
 * @param {number} refuelPercent - Refuel percentage
 * @returns {Object} Feedback object
 */
export const createTowCompletionFeedback = (destinationName, refuelPercent) => {
  return {
    message: `🚁 TOW COMPLETE - Arrived at ${destinationName} with ${refuelPercent}% fuel`,
    color: '#00ffff'
  };
};

/**
 * Calculate in-transit position when fuel depletes
 * @param {Object} origin - Origin location
 * @param {Object} destination - Destination location
 * @param {number} progressPercent - Progress percentage (0-100)
 * @returns {Object} In-transit position with coordinates
 */
export const calculateInTransitPosition = (origin, destination, progressPercent) => {
  const progress = progressPercent / 100;
  
  // If origin has coordinates, interpolate between them
  if (origin.x !== undefined && destination.x !== undefined) {
    return {
      name: 'In Transit',
      x: origin.x + (destination.x - origin.x) * progress,
      y: origin.y + (destination.y - origin.y) * progress,
      z: origin.z + (destination.z - origin.z) * progress,
      inTransit: true
    };
  }
  
  // Otherwise, use distance-based calculation
  const totalDistance = destination.distance || 0;
  const currentDistance = totalDistance * (1 - progress);
  
  return {
    name: 'In Transit',
    distance: currentDistance,
    inTransit: true
  };
};

/**
 * Check if tow service should be offered
 * @param {number} fuel - Current fuel level
 * @param {boolean} isWarping - Whether ship is currently warping
 * @returns {boolean} Whether to offer tow service
 */
export const shouldOfferTowService = (fuel, isWarping) => {
  return fuel <= 0 && isWarping;
};

/**
 * Get tow refuel percentage from config
 * @returns {number} Refuel percentage for tow service
 */
export const getTowRefuelPercent = () => {
  return GameConfig.credits.towRefuelPercent;
};

/**
 * Create location object for tow destination
 * @param {Object} destination - Destination system
 * @returns {Object} Location object with cleared coordinates
 */
export const createTowDestinationLocation = (destination) => {
  return {
    ...destination,
    x: undefined,
    y: undefined,
    z: undefined,
    inTransit: false
  };
};
